import React, { useContext, useEffect, useState } from "react";
import Responsive from "react-responsive-decorator";
import mapboxgl, { Map, LngLat, Marker, supported } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import inactiveMapMarker from "../assets/inactive-map-marker.svg";
import { CONSTANTS } from "../helpers";
import { Pharmacy, PharmacyResultsType } from "../@types";
import { config } from "../config";
import {
  Ga4Event,
  Ga4EventType,
  SearchResultType,
} from "../app/contexts/Analytics/types";
import { AppStore } from "../app/store";
import { AnalyticsActionType } from "../app/contexts/Analytics/types";

const { MAPBOX_ACCESS_TOKEN } = config;

interface PharmacySelectionMapProps {
  data: PharmacyResultsType;
  fallbackRadius?: number;
  onRadiusCache?: (radius: number) => void;
  page: number;
  selectedPharmacies: Pharmacy[];
}

const PharmacySelectionMap: React.FunctionComponent<PharmacySelectionMapProps> = props => {
  const {
    state: { planType, cwCoverage },
    dispatch,
  } = useContext(AppStore.AppContext);
  const [mapMarkers, setMapMarkers] = useState<Marker[]>([]);

  const updateMapMarkers = (): void => {
    mapMarkers.forEach(marker => {
      const element = marker.getElement();
      const npi = element.getAttribute("data-npi");
      if (npi && props.selectedPharmacies.map(p => p.npi).includes(npi)) {
        element.classList.add("PharmacySelectionMap__marker--active");
      } else {
        element.classList.remove("PharmacySelectionMap__marker--active");
      }
    });
  };

  useEffect(() => {
    const [
      sourceLng,
      sourceLat,
    ] = props.data.sourceAddress.geometry.coordinates;

    const metersToPixelsAtMaxZoom = (
      meters: number,
      latitude: number
    ): number => meters / 0.05 / Math.cos((latitude * Math.PI) / 180);

    if (supported() && MAPBOX_ACCESS_TOKEN) {
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN as string;
      const map = new Map({
        container: "PharmacySelectionMap",
        style: "mapbox://styles/mapbox/streets-v11",
        center: [sourceLng, sourceLat],
        zoom: 13,
      });

      map.getCanvas().setAttribute("tabindex", "-1");

      let startHoverTime = 0;

      map.on("mouseover", (): void => {
        startHoverTime = Date.now();
      });

      map.on("mouseout", (): void => {
        const timeSpentHovering = (Date.now() - startHoverTime) / 1000;

        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Find Plans - Add Pharmacy",
            event_label: `Map Hover - ${timeSpentHovering} seconds`,
          },
        });
      });

      map.on("load", () => {
        Array.from(
          document.querySelectorAll(".mapboxgl-control-container a")
        ).forEach(a => {
          a.setAttribute("tabindex", "-1");
        });

        const { features: pharmacyPoints } = props.data.pharmacies;

        if (pharmacyPoints.length) {
          map.addSource("pharmacies", {
            type: "geojson",
            data: props.data.pharmacies,
          });

          const lats: number[] = [];
          const longs: number[] = [];
          const markers: Marker[] = [];
          let farthestPharmacy = { lng: 0, lat: 0, distance_miles: 0 };

          pharmacyPoints.forEach((feature, index) => {
            const { geometry, properties } = feature;
            const { coordinates } = geometry as { coordinates: number[] };
            const { npi, distance_miles = 0 } = properties as Pharmacy;

            longs.push(coordinates[0]);
            lats.push(coordinates[1]);

            if (distance_miles > farthestPharmacy.distance_miles) {
              farthestPharmacy = {
                lng: coordinates[0],
                lat: coordinates[1],
                distance_miles,
              };
            }

            const pharmacyNumber =
              props.page * CONSTANTS.pharmacySelection.pageCount + (index + 1);

            const markerElement = document.createElement("div");
            markerElement.innerHTML = pharmacyNumber.toString();
            markerElement.className = "PharmacySelectionMap__marker";
            markerElement.setAttribute("data-npi", npi);
            markerElement.style.backgroundImage = `url(${inactiveMapMarker})`;
            markerElement.onclick = (): void => {
              const pharmacyListItem = document.getElementById(npi);
              const pharmacyNumber = pharmacyListItem?.querySelector(
                ".SelectPharmaciesPage__pharmacyNumber"
              )?.textContent;
              const mapClickText = `Map Click: Pharmacy #${(pharmacyNumber &&
                pharmacyNumber[0]) ||
                0}`;
              pharmacyListItem?.scrollIntoView({ behavior: "smooth" });
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Find Plans - Add Pharmacy",
                  event_label: mapClickText,
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.PHARMACY_MAP_ENGAGEMENT,
                  event_type: Ga4EventType.UI_INTERACTION,
                  text: mapClickText,
                  search_result_count: props.data.totalResults,
                  search_result_type: SearchResultType.MAP_CLICK,
                  mct_plan_type: planType,
                  mct_medigap: false,
                  mct_coverage_wizard: !!cwCoverage,
                },
              });
            };

            const marker = new Marker(markerElement)
              .setLngLat(new LngLat(coordinates[0], coordinates[1]))
              .addTo(map);

            markers.push(marker);
          });

          setMapMarkers(markers);

          if (lats.length && longs.length) {
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);
            const minLong = Math.min(...longs);
            const maxLong = Math.max(...longs);

            map.fitBounds(
              [
                [minLong - 0.01, minLat - 0.01],
                [maxLong + 0.01, maxLat + 0.01],
              ],
              { animate: false }
            );

            const srcLngLat = new LngLat(sourceLng, sourceLat);
            const farthestLngLat = new LngLat(
              farthestPharmacy.lng,
              farthestPharmacy.lat
            );

            const distanceBetween = srcLngLat.distanceTo(farthestLngLat);

            const distanceInPixels = metersToPixelsAtMaxZoom(
              distanceBetween,
              farthestPharmacy.lat
            );

            if (props.onRadiusCache) {
              props.onRadiusCache(distanceInPixels);
            }

            map.addLayer({
              id: "pharmacy-circle",
              source: {
                type: "geojson",
                data: {
                  type: "Feature",
                  geometry: {
                    type: "Point",
                    coordinates: [sourceLng, sourceLat],
                  },
                  properties: {},
                },
              },
              type: "circle",
              paint: {
                "circle-radius": {
                  stops: [
                    [0, 0],
                    [20, distanceInPixels],
                  ],
                  base: 2,
                },
                "circle-color": "#146A5D",
                "circle-stroke-color": "#146A5D",
                "circle-stroke-width": 1,
                "circle-opacity": 0.1,
                "circle-pitch-scale": "viewport",
              },
            });
          }
        } else {
          map.addLayer({
            id: "pharmacy-circle",
            source: {
              type: "geojson",
              data: {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [sourceLng, sourceLat],
                },
                properties: {},
              },
            },
            type: "circle",
            paint: {
              "circle-radius": {
                stops: [
                  [0, 0],
                  [20, props.fallbackRadius || 0],
                ],
                base: 2,
              },
              "circle-color": "#146A5D",
              "circle-stroke-color": "#146A5D",
              "circle-stroke-width": 1,
              "circle-opacity": 0.1,
              "circle-pitch-scale": "viewport",
            },
          });
        }

        map.resize();
      });
    }
  }, [props.data]);

  useEffect(() => {
    updateMapMarkers();
  }, [props.selectedPharmacies, mapMarkers]);

  return (
    <div id="PharmacySelectionMap" className="PharmacySelectionMap__map" />
  );
};

export default Responsive(PharmacySelectionMap);
